import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Phone, MapPin, ShieldCheck, ScanLine, Clock, TrainFront,
  SquareParking, Smile, Baby, Sparkles, BadgeCheck, GraduationCap, Stethoscope, Check, Heart,
} from "lucide-react";
import { Reveal, MaskedLines, Eyebrow, EASE } from "../components/Reveal";
import Marquee from "../components/Marquee";
import AppointmentForm from "../components/AppointmentForm";
import ReelVideo from "../components/ReelVideo";
import Testimonials from "../components/Testimonials";
import LearnMore from "../components/LearnMore";
import useSEO from "../hooks/useSEO";

const IMG = {
  hero: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=85&w=1600",
  treatment: "https://images.unsplash.com/photo-1663755489920-5e09f66d011a?auto=format&fit=crop&q=85&w=1400",
  smile: "https://images.unsplash.com/photo-1548661211-e559d8c17537?auto=format&fit=crop&q=85&w=1200",
  cbct: "https://images.pexels.com/photos/6812463/pexels-photo-6812463.jpeg?auto=compress&cs=tinysrgb&w=1200",
  gattuso: "https://customer-assets-v7afamib.emergentagent.net/job_chivasso-restyle/artifacts/1292b2ec0660050e_3%20foto%20Gattuso.webp",
  prevenzione: "/prevenzione-visita.jpg",
};

const STATS = [
  { icon: Clock, big: "6 ore", small: "denti fissi, se le condizioni lo consentono dopo visita specialistica e diagnosi radiologiche" },
  { icon: ScanLine, big: "TAC 3D", small: "ortopantomografo e dental scan in sede" },
  { icon: ShieldCheck, big: "100%", small: "impianti italiani certificati e tracciabili" },
  { icon: TrainFront, big: "700m", small: "dalla Stazione FS di Chivasso, con parcheggio" },
];

const BENTO = [
  {
    title: "Implantologia avanzata",
    desc: "Zigomatica, iuxtaossea, All-on-Four e All-on-Six: soluzioni per ogni condizione ossea, anche nei casi complessi.",
    img: IMG.treatment,
    span: "lg:col-span-7 min-h-[420px]",
    testid: "bento-implantologia",
  },
  {
    title: "Ortodonzia invisibile Invisalign®",
    desc: "Allineatori trasparenti e brackets tradizionali, per bambini e adulti.",
    icon: Smile,
    span: "lg:col-span-5 min-h-[420px]",
    testid: "bento-ortodonzia",
  },
  {
    title: "Protesi e faccette",
    desc: "Zirconio-ceramica, lega-ceramica, protesi rimovibili con attacchi o barre.",
    icon: Sparkles,
    span: "lg:col-span-4 min-h-[300px]",
    testid: "bento-protesi",
  },
  {
    title: "Diagnostica in sede",
    desc: "TAC dental scan e ortopantomografo: nessuna attesa, piano di cura subito.",
    icon: ScanLine,
    span: "lg:col-span-4 min-h-[300px]",
    testid: "bento-diagnostica",
  },
  {
    title: "Igiene e parodontologia",
    desc: "Pulizia professionale e trattamento della parodontite per gengive sane.",
    icon: Stethoscope,
    span: "lg:col-span-4 min-h-[300px]",
    testid: "bento-igiene",
  },
  {
    title: "Odontoiatria pediatrica",
    desc: "I primi denti meritano un'attenzione speciale: visite delicate e serietà per i più piccoli.",
    icon: Baby,
    span: "lg:col-span-12 min-h-[180px]",
    testid: "bento-pedodonzia",
  },
];

export default function Home() {
  useSEO({
    title: "Dentista Chivasso | Dentalchivasso — Implantologia, All-on-4, Ortodonzia Invisibile",
    description: "Studio Dentistico Dentalchivasso a Chivasso (TO): implantologia avanzata con denti fissi in 6 ore a carico immediato, All-on-Four e All-on-Six, protesi e Invisalign. TAC 3D in sede, a 700m dalla stazione FS con parcheggio.",
    path: "/",
  });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const heroVideoRef = useRef(null);
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = true;
    const play = () => v.play().catch(() => {});
    if (v.readyState >= 2) play();
    v.addEventListener("loadeddata", play);
    v.addEventListener("canplay", play);
    return () => {
      v.removeEventListener("loadeddata", play);
      v.removeEventListener("canplay", play);
    };
  }, []);

  return (
    <main>
      {/* ---- HERO ---- */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="home-hero">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-teal-mist blur-3xl opacity-70" />
        <div className="absolute top-1/3 -left-52 w-[420px] h-[420px] rounded-full bg-goldsoft/60 blur-3xl opacity-60" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-32 pb-20 grid lg:grid-cols-12 gap-14 items-center w-full">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Eyebrow>Studio Dentistico · Chivasso (TO)</Eyebrow>
            </motion.div>

            <MaskedLines
              className="mt-6 font-display font-medium tracking-tight text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
              delay={0.25}
              lines={[
                "Il tuo sorriso,",
                "costruito con",
                <span key="3" className="italic text-teal">precisione clinica.</span>,
              ]}
            />

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
              className="mt-7 max-w-xl text-base md:text-lg text-mutedc leading-relaxed"
            >
              Implantologia avanzata con carico immediato, protesi e ortodonzia invisibile.
              Diagnostica TAC 3D direttamente in sede, impianti italiani certificati
              e l'attenzione di uno studio di famiglia.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contatti"
                data-testid="hero-cta-prenota"
                className="group inline-flex items-center gap-2 rounded-full bg-teal text-white font-semibold text-sm px-7 py-4 hover:bg-teal-deep transition-colors duration-300"
              >
                Prenota una visita
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:01119667070"
                data-testid="hero-cta-phone"
                className="inline-flex items-center gap-2.5 rounded-full border border-line bg-paper px-7 py-4 font-semibold text-sm text-ink hover:border-teal hover:text-teal transition-colors duration-300"
              >
                <Phone size={16} className="text-teal" />
                011 19667070
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-mutedc"
            >
              <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-teal" /> Via Gerbido 15/3, Chivasso</span>
              <span className="inline-flex items-center gap-1.5"><TrainFront size={13} className="text-teal" /> A 700m dalla Stazione FS</span>
              <span className="inline-flex items-center gap-1.5"><SquareParking size={13} className="text-teal" /> Parcheggio per i pazienti</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.3, ease: EASE }}
              className="mt-6"
            >
              <LearnMore to="/servizi" testid="learn-more-hero" />
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: EASE }}
              className="relative rounded-[2.5rem] overflow-hidden border border-line shadow-lift aspect-[4/5] max-h-[600px] w-full"
              data-testid="hero-image-frame"
            >
              <motion.video
                ref={heroVideoRef}
                poster="/videos/hero-chivasso-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                style={{ y: imgY }}
                className="absolute inset-0 w-full h-[116%] object-cover"
                data-testid="hero-video"
              >
                <source src="/videos/hero-chivasso.webm" type="video/webm" />
                <source src="/videos/hero-chivasso.mp4" type="video/mp4" />
              </motion.video>
              <div className="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
              className="absolute -bottom-6 -left-6 md:-left-10 rounded-2xl bg-paper border border-line shadow-lift px-6 py-4"
              data-testid="hero-floating-badge"
            >
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-mutedc">Carico immediato</p>
              <p className="font-display text-3xl font-semibold text-teal">6 ore</p>
              <p className="mt-1 text-[0.58rem] text-mutedc leading-snug max-w-[220px]">denti fissi, se le condizioni lo consentono dopo una visita specialistica e diagnosi radiologiche</p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* ---- BENTO SERVIZI ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32" data-testid="home-servizi-section">
        <Reveal>
          <Eyebrow>I nostri servizi</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl max-w-2xl leading-[1.08]">
            Tutto ciò che il tuo sorriso richiede, <span className="italic text-teal">sotto lo stesso tetto.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
          {BENTO.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.06} className={b.span}>
              <Link
                to="/servizi"
                data-testid={b.testid}
                className="group relative h-full w-full rounded-3xl border border-line bg-paper overflow-hidden flex flex-col justify-end p-7 hover:shadow-lift transition-shadow duration-500"
              >
                {b.img && (
                  <>
                    <img src={b.img} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
                  </>
                )}
                {!b.img && (
                  <b.icon size={30} className="absolute top-7 left-7 text-teal" strokeWidth={1.6} />
                )}
                <div className="relative">
                  <h3 className={`font-display text-2xl md:text-[1.7rem] font-semibold tracking-tight ${b.img ? "text-ivory" : "text-ink"}`}>
                    {b.title}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed max-w-md ${b.img ? "text-ivory/75" : "text-mutedc"}`}>
                    {b.desc}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/servizi" testid="learn-more-bento" />
        </div>
      </section>

      {/* ---- IMPLANTOLOGIA ZIGOMATICA ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32" data-testid="home-zigomatica-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="grid grid-cols-2 gap-4 items-center">
              <div className="relative rounded-[1.5rem] overflow-hidden border border-line shadow-lift bg-ink" data-testid="zigomatica-3d-frame">
                <img
                  src="/zigomatica-3d.jpg"
                  alt="Schema degli impianti zigomatici ancorati nell'osso zigomatico"
                  className="w-full aspect-[4/3] object-cover"
                />
                <span className="absolute bottom-3 left-3 rounded-full bg-ink/70 text-ivory text-[0.62rem] font-semibold uppercase tracking-[0.14em] px-3 py-1.5 backdrop-blur">
                  Impianti zigomatici
                </span>
              </div>
              <div className="relative rounded-[1.5rem] overflow-hidden border border-line shadow-lift bg-ink" data-testid="zigomatica-rx-frame">
                <img
                  src="/zigomatica-rx.jpg"
                  alt="Radiografia panoramica con impianti zigomatici e barre"
                  className="w-full aspect-[4/3] object-cover"
                />
                <span className="absolute bottom-3 left-3 rounded-full bg-ink/70 text-ivory text-[0.62rem] font-semibold uppercase tracking-[0.14em] px-3 py-1.5 backdrop-blur">
                  Radiografia reale
                </span>
              </div>
            </div>
            <Reveal delay={0.1}>
              <Link
                to="/studio"
                data-testid="zigomatica-giorgi-link"
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-teal hover:text-teal-deep group"
              >
                Seguito dal Dr. Matteo Giorgi, punto di riferimento in Italia e all'estero
                <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </Reveal>

          <div>
            <Reveal>
              <Eyebrow>Implantologia zigomatica in carenza d'osso</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Ti hanno detto che hai <span className="italic text-teal">poco osso?</span>
              </h2>
              <p className="mt-6 text-mutedc leading-relaxed max-w-xl text-base md:text-lg">
                Vuoi liberarti delle protesi mobili — totali, parziali o scheletrate?
                Gli impianti zigomatici si ancorano nell'osso zigomatico, più denso e stabile:
                denti fissi anche in carenza d'osso, senza lunghi innesti.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-7 space-y-3">
                {[
                  "Soluzione per chi è stato escluso dagli impianti tradizionali",
                  "Addio protesi mobili: totali, parziali o scheletrate",
                  "Pianificazione su TAC 3D fatta direttamente in sede",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check size={16} className="text-teal mt-0.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <Link
                to="/contatti"
                data-testid="zigomatica-cta"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-ink text-ivory font-semibold text-sm px-7 py-4 hover:bg-teal transition-colors duration-300"
              >
                Prenota una consulenza
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/servizi#implantologia" testid="learn-more-zigomatica" />
        </div>
      </section>

      {/* ---- DAY SURGERY ---- */}
      <section className="bg-sand/70 border-y border-line" data-testid="home-daysurgery-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full rounded-[2rem] border border-gold/40" />
              <img
                src="/day-surgery.jpg"
                alt="Intervento di implantologia avanzata in centro Day Surgery"
                className="relative rounded-[2rem] w-full aspect-[4/3] object-cover border border-line shadow-lift"
                data-testid="daysurgery-photo"
              />
              <span className="absolute bottom-3 left-3 rounded-full bg-ink/70 text-ivory text-[0.62rem] font-semibold uppercase tracking-[0.14em] px-3 py-1.5 backdrop-blur">
                Sala operatoria · Day Surgery
              </span>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <Eyebrow>Day Surgery</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Interventi di implantologia avanzata <span className="italic text-teal">in Day Surgery.</span>
              </h2>
              <p className="mt-6 text-mutedc leading-relaxed max-w-xl">
                Gli interventi di implantologia avanzata più impegnativi — oltre 3 ore — vengono
                effettuati in un centro Day Surgery convenzionato con Dentalchivasso: massima
                sicurezza, sale attrezzate e recupero comodo, con il nostro team che ti segue
                in ogni fase.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-7 space-y-3">
                {[
                  "Sala operatoria con team medico completo",
                  "Sala degenza dedicata al recupero post-operatorio",
                  "Centro convenzionato con Dentalchivasso",
                  "Per gli interventi di implantologia avanzata di oltre 3 ore",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check size={16} className="text-teal mt-0.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <Link
                to="/contatti"
                data-testid="daysurgery-cta"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-teal text-white font-semibold text-sm px-7 py-4 hover:bg-teal-deep transition-colors duration-300"
              >
                Prenota una consulenza
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/servizi#implantologia" testid="learn-more-daysurgery" />
        </div>
      </section>

      {/* ---- DOPO L'INTERVENTO: CONTROLLI (REEL) ---- */}
      <section className="bg-ink text-ivory relative overflow-hidden grain" data-testid="home-controlli-section">
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-teal/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="max-w-[320px] mx-auto w-full space-y-4">
            <ReelVideo
              src="/videos/reel_controlli.mp4"
              poster="/videos/reel_controlli_poster.jpg"
              caption="Dopo l'implantologia: i controlli periodici proteggono il risultato."
              testid="reel-controlli"
            />
            <img
              src="/lavoro-riggio.jpg"
              alt="Manuel Riggio e Silvia al lavoro in studio"
              className="w-full aspect-[4/3] object-cover rounded-3xl border border-line shadow-soft"
              data-testid="lavoro-riggio-photo"
            />
            <p className="text-center text-xs text-ivory/60">Manuel e Silvia al lavoro, ogni giorno per il tuo sorriso.</p>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow light>Dopo l'intervento</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl leading-[1.08]">
                Hai fatto un intervento e ti senti bene. <span className="italic text-teal-mist">Adesso protegge i controlli.</span>
              </h2>
              <p className="mt-6 text-ivory/65 leading-relaxed max-w-xl">
                Un impianto resta in bocca per anni se viene mantenuto: i controlli periodici e
                l'igiene professionale monitorano impianti e gengive, così continui a sentirti
                bene nel tempo.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-7 space-y-3">
                {[
                  "Controlli programmati su misura per il tuo piano di cura",
                  "Igiene professionale per impianti e protesi",
                  "Monitoraggio TAC quando serve, direttamente in sede",
                ].map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-ivory/85">
                    <Check size={16} className="text-gold mt-0.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <Link
                to="/contatti"
                data-testid="controlli-cta"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-teal text-white font-semibold text-sm px-7 py-4 hover:bg-teal-deep transition-colors duration-300"
              >
                Prenota un controllo
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/servizi#parodontologia" testid="learn-more-controlli" />
        </div>
      </section>

      {/* ---- DIAGNOSTICA + STATS ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32" data-testid="home-diagnostica-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="absolute -top-5 -left-5 w-full h-full rounded-[2rem] border border-gold/40" />
            <img
              src={IMG.cbct}
              alt="Diagnostica TAC 3D in sede"
              className="relative rounded-[2rem] w-full aspect-[4/3] object-cover border border-line shadow-soft"
              data-testid="diagnostica-image"
            />
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow>Diagnostica in sede</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Vedi il tuo piano di cura <span className="italic text-teal">dalla prima visita.</span>
              </h2>
              <p className="mt-6 text-mutedc leading-relaxed">
                Ortopantomografo digitale e TAC dental scan direttamente nel nostro studio:
                nessun appuntamento altrove, nessuna attesa. Analizziamo insieme le immagini
                e costruiamo il tuo preventivo il giorno stesso.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {STATS.map((s, i) => (
            <Reveal key={s.big} delay={i * 0.08}>
              <div className="rounded-3xl border border-line bg-paper p-7 h-full hover:border-teal/40 transition-colors duration-300" data-testid={`stat-card-${i}`}>
                <s.icon size={22} className="text-gold" strokeWidth={1.8} />
                <p className="mt-4 font-display text-4xl md:text-[2.6rem] font-semibold text-ink">{s.big}</p>
                <p className="mt-2 text-xs text-mutedc leading-relaxed">{s.small}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/servizi#implantologia" testid="learn-more-diagnostica" />
        </div>
      </section>

      {/* ---- PREZZI ---- */}
      <section className="bg-ink text-ivory relative overflow-hidden grain" data-testid="home-prezzi-section">
        <div className="absolute -bottom-40 -right-32 w-[480px] h-[480px] rounded-full bg-teal/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
          <Reveal>
            <Eyebrow light>Prezzi trasparenti</Eyebrow>
            <h2 className="mt-5 font-display font-medium tracking-tight text-4xl md:text-5xl max-w-2xl leading-[1.08]">
              Chiarezza sul costo, <span className="italic text-teal-mist">prima di iniziare.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl">
            <Reveal>
              <div className="rounded-3xl border border-teal/40 bg-teal/10 p-9 h-full" data-testid="price-card-carico">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-mist">Il più richiesto</p>
                <p className="mt-5 font-display text-5xl font-semibold">€ 5.900</p>
                <p className="mt-2 font-semibold">Implantologia a carico immediato</p>
                <p className="mt-3 text-sm text-ivory/65 leading-relaxed">
                  Denti fissi in 6 ore, se le condizioni lo consentono dopo una visita specialistica e diagnosi radiologiche.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-ivory/15 bg-ivory/5 p-9 h-full" data-testid="price-card-overdenture">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Protesi rimovibile</p>
                <p className="mt-5 font-display text-5xl font-semibold">da € 3.900</p>
                <p className="mt-2 font-semibold">Overdenture su 2 impianti</p>
                <p className="mt-3 text-sm text-ivory/65 leading-relaxed">
                  Protesi rimovibile stabilizzata con attacchi o barra, con impianti italiani certificati.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm text-ivory/50 flex items-center gap-2.5" data-testid="price-note">
              <BadgeCheck size={16} className="text-gold" />
              Ogni preventivo è personalizzato e confermato dopo la prima visita con TAC. Impianti 100% italiani, certificati e tracciabili.
            </p>
          </Reveal>
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/prezzi" testid="learn-more-prezzi" />
        </div>
      </section>

      {/* ---- DOTTOR GATTUSO ---- */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32" data-testid="home-team-section">
        <Reveal>
          <Eyebrow>Chi ti seguirà</Eyebrow>
          <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl max-w-2xl leading-[1.08]">
            Un'esperienza che fa <span className="italic text-teal">la differenza.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="relative rounded-[2rem] overflow-hidden border border-line shadow-lift h-full min-h-[480px]" data-testid="gattuso-card">
              <img
                src={IMG.gattuso}
                alt="Dottor Massimo Filippo Gattuso"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <p className="font-display text-3xl text-ivory font-semibold">Dott. Massimo Filippo Gattuso</p>
                <p className="mt-1 text-sm text-teal-mist font-semibold">Medico Chirurgo oro-maxillo-facciale · Implantologia avanzata</p>
                <p className="mt-3 text-sm text-ivory/70 leading-relaxed max-w-md">
                  25 anni di esperienza, docente al Master dell'Università La Sapienza di Roma.
                  Si occupa degli interventi di implantologia avanzata nel nostro studio da 8 anni.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-5 content-stretch">
            {[
              { icon: Smile, t: "Ortodonzia e pedodonzia", d: "Invisalign®, brackets e apparecchi per i più piccoli, con percorsi su misura." },
              { icon: Sparkles, t: "Protesi ed estetica", d: "Protesi fisse in zirconio, faccette e ricostruzioni estetiche del sorriso." },
              { icon: Stethoscope, t: "Parodontologia e igiene", d: "Trattamento della parodontite e manutenzione professionale periodica." },
              { icon: GraduationCap, t: "Aggiornamento continuo", d: "Il team si forma costantemente su tecniche e materiali certificati." },
            ].map((m, i) => (
              <Reveal key={m.t} delay={i * 0.07} className="h-full">
                <div className="rounded-3xl border border-line bg-paper p-7 h-full flex flex-col hover:border-teal/40 transition-colors duration-300" data-testid={`team-role-card-${i}`}>
                  <m.icon size={26} className="text-teal" strokeWidth={1.7} />
                  <p className="mt-4 font-display text-xl font-semibold text-ink">{m.t}</p>
                  <p className="mt-2 text-sm text-mutedc leading-relaxed">{m.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="riggio-card">
            <img
              src="/team-riggio-v2.jpg"
              alt="Manuel Riggio, igienista dentale e direttore di Clinica"
              className="md:col-span-2 w-full h-full min-h-[300px] max-h-[460px] object-cover rounded-xl"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Direzione clinica & relazioni con i pazienti</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Manuel Riggio</p>
              <p className="mt-2 text-sm text-teal font-semibold">Igienista dentale e direttore di Clinica</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Da 10 anni si occupa del rapporto con i pazienti: ti accoglie, ti segue e rende
                ogni visita semplice e chiara, dal primo contatto ai controlli periodici.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="giorgi-card">
            <img
              src="/team-giorgi.jpg"
              alt="Dr. Matteo Giorgi, odontoiatra e chirurgo orale"
              className="md:col-span-2 w-full h-full min-h-[280px] max-h-[380px] object-cover object-top md:order-2"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center md:order-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Chirurgia orale & implantologia in carenza d'osso</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Dr. Matteo Giorgi</p>
              <p className="mt-2 text-sm text-teal font-semibold">Odontoiatra e chirurgo orale</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Da oltre 10 anni si occupa di implantologia zigomatica e iuxtaossea in carenza
                d'osso. Punto di riferimento in Italia e all'estero, relatore in convegni in
                tutto il mondo.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="hola-card">
            <div className="md:col-span-2 grid grid-rows-2 gap-2 h-full min-h-[280px] max-h-[380px]">
              <img
                src="/team-hola.jpg"
                alt="Dr. Dritan Kola, odontoiatra"
                className="w-full h-full object-cover object-top rounded-xl"
              />
              <img
                src="/lavoro-hola.jpg"
                alt="Dr. Kola e Silvia al lavoro in studio"
                className="w-full h-full object-cover rounded-xl"
                data-testid="lavoro-hola-photo"
              />
            </div>
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Protesi · Endodonzia · Conservativa</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Dr. Dritan Kola</p>
              <p className="mt-2 text-sm text-teal font-semibold">Odontoiatra</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Si occupa di protesi, endodonzia, conservativa e piccola chirurgia: i trattamenti
                di ogni giorno eseguiti con precisione, per mantenere il sorriso sano nel tempo.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="rounded-[2rem] border border-line bg-paper overflow-hidden grid md:grid-cols-5" data-testid="mazzariol-card">
            <img
              src="/team-mazzariol-hd.jpg"
              alt="Dr.ssa Patricia Mazzariol, odontoiatra"
              className="md:col-span-2 w-full h-full min-h-[300px] max-h-[420px] object-cover md:order-2"
            />
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center md:order-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Odontoiatria generale & estetica</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Dr.ssa Patricia Mazzariol</p>
              <p className="mt-2 text-sm text-teal font-semibold">Odontoiatra</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-xl">
                Da oltre 20 anni si occupa di odontoiatria generale ed estetica: faccette
                estetiche e ricostruzioni per un sorriso naturale, curato nei minimi dettagli.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="rounded-[2rem] border border-line bg-paper overflow-hidden" data-testid="staff-card">
            <div className="grid grid-cols-2">
              <div className="relative">
                <img
                  src="/team-francesca.jpg"
                  alt="Francesca, ASO e receptionist"
                  className="w-full h-full min-h-[260px] max-h-[420px] object-cover object-top"
                  data-testid="francesca-photo"
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-ink/70 text-ivory text-xs font-semibold px-4 py-1.5 backdrop-blur">
                  Francesca · Reception
                </span>
              </div>
              <div className="relative">
                <img
                  src="/team-silvia-v2.jpg"
                  alt="Silvia, ASO, nella sala operatoria"
                  className="w-full h-full min-h-[260px] max-h-[420px] object-cover"
                  data-testid="silvia-photo"
                />
                <span className="absolute bottom-4 left-4 rounded-full bg-teal text-white text-xs font-semibold px-4 py-1.5">
                  Silvia · ASO
                </span>
              </div>
            </div>
            <div className="p-8 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Accoglienza & assistenza allo studio</p>
              <p className="mt-3 font-display text-3xl md:text-4xl font-semibold text-ink">Francesca e Silvia</p>
              <p className="mt-4 text-sm md:text-base text-mutedc leading-relaxed max-w-3xl">
                <strong className="text-ink">Francesca</strong>, ASO e receptionist, si occupa dell'accoglienza
                dei pazienti e del customer care: ogni visita inizia e finisce con lei.
                <strong className="text-ink"> Silvia</strong>, ASO, assiste il medico in tutte le fasi del
                trattamento. Entrambe si dedicano a mantenere lo studio in perfette condizioni
                e alla sterilizzazione degli strumenti.
              </p>
            </div>
          </div>
        </Reveal>
        <div className="mt-10 text-center">
          <LearnMore to="/studio" testid="learn-more-team" />
        </div>
      </section>

      {/* ---- PREVENZIONE ---- */}
      <section className="bg-teal-mist/40 border-y border-line" data-testid="home-prevenzione-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full rounded-[2rem] border border-teal/30" />
              <img
                src="/prevenzione-visita.jpg"
                alt="Dentalchivasso — Una visita di igiene e prevenzione con il team dello studio"
                className="relative rounded-[2rem] w-full max-w-[480px] aspect-square object-cover border border-line shadow-lift"
                data-testid="prevenzione-image"
              />
            </div>
          </Reveal>
          <div className="order-1 lg:order-2">
            <Reveal>
              <Eyebrow>Prevenzione</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Il sorriso inizia <span className="italic text-teal">dalla prevenzione.</span>
              </h2>
              <p className="mt-6 text-mutedc leading-relaxed max-w-xl">
                Prendersi cura dell'igiene orale ogni giorno è il primo passo verso un
                sorriso sano e duraturo. Con i nostri programmi di igiene e controllo,
                la prevenzione diventa un'abitudine semplice.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-8 space-y-5">
                {[
                  { icon: ShieldCheck, t: "Preveni", d: "le carie e le malattie gengivali con controlli regolari." },
                  { icon: Sparkles, t: "Mantieni", d: "denti sani e gengive forti con l'igiene professionale." },
                  { icon: Heart, t: "Sorridi", d: "con sicurezza, ogni giorno." },
                ].map((p) => (
                  <li key={p.t} className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-full bg-paper border border-line flex items-center justify-center shrink-0">
                      <p.icon size={20} className="text-teal" strokeWidth={1.8} />
                    </span>
                    <p className="text-sm md:text-base text-ink/85 leading-relaxed pt-2">
                      <strong className="text-ink uppercase tracking-wide text-xs font-bold">{p.t}</strong> · {p.d}
                    </p>
                  </li>
                ))}
              </ul>
              <Link
                to="/contatti"
                data-testid="prevenzione-cta"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-teal text-white font-semibold text-sm px-7 py-4 hover:bg-teal-deep transition-colors duration-300"
              >
                Prenota l'igiene professionale
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
        <div className="mt-10 text-center">
          <LearnMore to="/servizi#parodontologia" testid="learn-more-prevenzione" />
        </div>
      </section>

      <Testimonials />

      {/* ---- PRENOTA ---- */}
      <section id="prenota" className="bg-teal-mist/50 border-y border-line" data-testid="home-prenota-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Reveal>
              <Eyebrow>Prenota una visita</Eyebrow>
              <h2 className="mt-5 font-display font-medium tracking-tight text-ink text-4xl md:text-5xl leading-[1.08]">
                Il primo passo è <span className="italic text-teal">una conversazione.</span>
              </h2>
              <p className="mt-6 text-mutedc leading-relaxed max-w-lg">
                Raccontaci la tua esigenza: ti richiamiamo per fissare la prima visita
                con TAC e darti un preventivo chiaro, senza impegno.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-4 text-sm">
                <a href="tel:01119667070" data-testid="prenota-phone-link" className="flex items-center gap-3 font-semibold text-ink hover:text-teal transition-colors">
                  <span className="w-10 h-10 rounded-full bg-paper border border-line flex items-center justify-center"><Phone size={16} className="text-teal" /></span>
                  011 19667070
                </a>
                <p className="flex items-center gap-3 text-mutedc">
                  <span className="w-10 h-10 rounded-full bg-paper border border-line flex items-center justify-center shrink-0"><MapPin size={16} className="text-teal" /></span>
                  Via Gerbido 15/3, 10034 Chivasso (TO) — a 700m dalla Stazione FS
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <AppointmentForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}